/*
 * Toasts: abajo a la izquierda, uno a la vez, con deshacer cuando aplica.
 * Nunca llevan errores que exijan una decisión; esos se pintan en el sitio.
 */

import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from "react";

type Aviso = { msg: string; deshacer?: () => void };
type Ctx = { avisar: (msg: string, deshacer?: () => void) => void; deshacer: () => void };

const AvisoCtx = createContext<Ctx | null>(null);

export function ProveedorAvisos({ children }: { children: ReactNode }) {
  const [aviso, setAviso] = useState<Aviso | null>(null);
  const timer = useRef<number | undefined>(undefined);
  const deshacerRef = useRef<(() => void) | undefined>(undefined);

  const avisar = useCallback((msg: string, deshacer?: () => void) => {
    deshacerRef.current = deshacer;
    setAviso({ msg, deshacer });
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => { setAviso(null); deshacerRef.current = undefined; }, deshacer ? 10000 : 4000);
  }, []);

  const deshacer = useCallback(() => {
    const fn = deshacerRef.current;
    if (!fn) return;
    deshacerRef.current = undefined;
    fn();
    avisar("Deshecho");
  }, [avisar]);

  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "z" && deshacerRef.current) { e.preventDefault(); deshacer(); }
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [deshacer]);

  return (
    <AvisoCtx.Provider value={{ avisar, deshacer }}>
      {children}
      {aviso && (
        <div role="status" style={{ position: "fixed", left: 20, bottom: 20, zIndex: 90, display: "flex", alignItems: "center", gap: 14, background: "var(--navy)", color: "#fff", padding: "11px 16px", borderRadius: "var(--r-control)", fontSize: 13, boxShadow: "0 8px 24px rgba(11,29,58,.25)", maxWidth: "min(560px, calc(100vw - 40px))" }}>
          <span>{aviso.msg}</span>
          {aviso.deshacer && (
            <button onClick={deshacer} style={{ color: "var(--ice)", fontWeight: 500, whiteSpace: "nowrap" }}>
              Deshacer <span style={{ opacity: 0.7, fontFamily: "var(--font-mono)", fontSize: 11 }}>⌘Z</span>
            </button>
          )}
        </div>
      )}
    </AvisoCtx.Provider>
  );
}

export function useAvisos(): Ctx {
  const c = useContext(AvisoCtx);
  if (!c) throw new Error("useAvisos fuera de ProveedorAvisos");
  return c;
}
